/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
import React, { useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { mergeRefs } from 'use-callback-ref';

import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import { makeKeyMapWithCommon, tooltip } from '@atlaskit/editor-common/keymaps';
import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { DragHandleButton } from '@atlaskit/pragmatic-drag-and-drop-react-accessibility/drag-handle-button';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const moveRowUp = makeKeyMapWithCommon('Move Up', 'Ctrl-Alt-Shift-[');
const moveRowDown = makeKeyMapWithCommon('Move Down', 'Ctrl-Alt-Shift-]');

const upKeyMap = tooltip(moveRowUp);
const downKeyMap = tooltip(moveRowDown);

const dragHandleStyles = xcss({
	backgroundColor: 'utility.elevation.surface.current',
	marginInline: 'space.050',
});

const draggingStyles = xcss({
	opacity: '0.5',
});

const shortcut = xcss({
	background: token('color.background.neutral'),
	color: 'color.text.subtle',
	borderRadius: 'radius.small',
	padding: 'space.050',
	alignSelf: 'flex-end',
});

const getStyles = (isDragHandleHovered: boolean, isDragHandleFocused: boolean) => ({
	justifyContent: 'center',
	width: '16px',
	backgroundColor: token('utility.elevation.surface.current'),
	...(isDragHandleHovered && {
		backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
	}),
	...(isDragHandleFocused && {
		backgroundColor: token('utility.elevation.surface.current'),
	}),
});

export type DragHandleMenuProps = {
	isDragging?: boolean;
	isDragHandleFocused: boolean;
	isFirst: boolean;
	isLast: boolean;
	onBlur(): void;
	onFocus(): void;
	onMoveDown(): void;
	onMoveUp(): void;
	setIsDragHandleFocused(focused: boolean): void;
};

export const DragHandleMenu = ({
	isDragHandleFocused,
	isDragging,
	isFirst,
	isLast,
	onMoveUp,
	onMoveDown,
	setIsDragHandleFocused,
	onFocus,
	onBlur,
}: DragHandleMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const dragHandleRef = useRef<HTMLButtonElement>(null);

	const [isDragHandleHovered, setIsDragHandleHovered] = useState<boolean>(false);

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...triggerProps }) => (
				<Box xcss={[dragHandleStyles, isDragging && draggingStyles]}>
					<DragHandleButton
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={getStyles(isDragHandleHovered, isDragHandleFocused)}
						ref={mergeRefs([dragHandleRef, triggerRef])}
						{...triggerProps}
						testId="drag-handle"
						label={formatMessage(messages.reorder)}
						onMouseLeave={() => {
							if (!triggerProps.isSelected) {
								setIsDragHandleHovered(false);
							}
						}}
						onMouseEnter={() => {
							if (!triggerProps.isSelected) {
								setIsDragHandleHovered(true);
							}
						}}
						onFocus={onFocus}
						onBlur={onBlur}
					/>
				</Box>
			)}
			onOpenChange={(args: OnOpenChangeArgs) => {
				const { isOpen } = args;
				setIsDragHandleFocused(isOpen);
				if (!isOpen) {
					setIsDragHandleHovered(false);
				}
			}}
			isOpen={isDragHandleFocused}
		>
			<DropdownItemGroup>
				<DropdownItem
					elemBefore={<ArrowUpIcon label={formatMessage(messages.arrowUp)} />}
					elemAfter={<Box xcss={shortcut}>{upKeyMap}</Box>}
					isDisabled={isFirst}
					onClick={onMoveUp}
				>
					<FormattedMessage {...messages.moveUp} />
				</DropdownItem>
				<DropdownItem
					elemBefore={<ArrowDownIcon label={formatMessage(messages.arrowDown)} />}
					elemAfter={<Box xcss={shortcut}>{downKeyMap}</Box>}
					isDisabled={isLast}
					onClick={onMoveDown}
				>
					<FormattedMessage {...messages.moveDown} />
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
